import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  AlertTitle,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  OpenInNew as OpenInNewIcon,
  TrendingUp as TrendingUpIcon,
  MoneyOff as MoneyOffIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  AccessTime as AccessTimeIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import cashSettlementService from '../../services/cashSettlementService';

// ═══════════════════════════════════════════════════════════════
// Constantes
// ═══════════════════════════════════════════════════════════════

const STATUS_OPTIONS = [
  { value: '', labelKey: 'adminCashSettlement.filters.allStatuses' },
  { value: 'pending', labelKey: 'adminCashSettlement.filters.pending' },
  { value: 'payer_confirmed', labelKey: 'adminCashSettlement.filters.payerConfirmed' },
  { value: 'completed', labelKey: 'adminCashSettlement.filters.completed' },
  { value: 'expired', labelKey: 'adminCashSettlement.filters.expired' },
  { value: 'disputed', labelKey: 'adminCashSettlement.filters.disputed' },
  { value: 'cancelled', labelKey: 'adminCashSettlement.filters.cancelled' }
];

const STATUS_CHIP_CONFIG = {
  pending: { color: 'warning', icon: <HourglassIcon fontSize="small" /> },
  payer_confirmed: { color: 'info', icon: <CheckCircleIcon fontSize="small" /> },
  completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  expired: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
  disputed: { color: 'error', icon: <GavelIcon fontSize="small" /> },
  cancelled: { color: 'default', icon: <CancelIcon fontSize="small" /> }
};

// ═══════════════════════════════════════════════════════════════
// Composant principal
// ═══════════════════════════════════════════════════════════════

const AdminCashSettlementPage = () => {
  const { t, i18n } = useTranslation('disputes');
  const { enqueueSnackbar } = useSnackbar();
  const dateLocale = i18n?.language?.startsWith('fr') ? fr : enUS;

  // State — Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // State — List
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');

  // State — Action menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSettlement, setMenuSettlement] = useState(null);

  // State — Force expire dialog
  const [expireDialogOpen, setExpireDialogOpen] = useState(false);
  const [expireTarget, setExpireTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // ═════════════════════════════════════════════════
  // Data fetching
  // ═════════════════════════════════════════════════

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await cashSettlementService.adminStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch cash settlement stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await cashSettlementService.adminList({
        status: statusFilter || undefined,
        search: searchDebounced || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        amountMin: amountMin || undefined,
        amountMax: amountMax || undefined,
        sortBy,
        sortOrder,
        page,
        limit: 20
      });
      setSettlements(result.data || []);
      setTotalPages(result.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch cash settlement list:', err);
      setError(t('adminCashSettlement.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchDebounced, dateFrom, dateTo, amountMin, amountMax, sortBy, sortOrder, page, t]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleRefresh = () => {
    fetchStats();
    fetchList();
  };

  // ═════════════════════════════════════════════════
  // Actions
  // ═════════════════════════════════════════════════

  const handleMenuOpen = (event, settlement) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSettlement(settlement);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSettlement(null);
  };

  const handleResendPayer = async (settlement) => {
    handleMenuClose();
    try {
      setActionLoading(true);
      await cashSettlementService.adminResend(settlement.id, 'payer_reminder');
      enqueueSnackbar(t('adminCashSettlement.actions.resendPayerSuccess'), { variant: 'success' });
      fetchList();
      fetchStats();
    } catch (err) {
      enqueueSnackbar(
        err?.message || t('adminCashSettlement.actions.resendError'),
        { variant: 'error' }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendRecipient = async (settlement) => {
    handleMenuClose();
    try {
      setActionLoading(true);
      await cashSettlementService.adminResend(settlement.id, 'recipient_reminder');
      enqueueSnackbar(t('adminCashSettlement.actions.resendRecipientSuccess'), { variant: 'success' });
      fetchList();
      fetchStats();
    } catch (err) {
      enqueueSnackbar(
        err?.message || t('adminCashSettlement.actions.resendError'),
        { variant: 'error' }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenExpireDialog = (settlement) => {
    handleMenuClose();
    setExpireTarget(settlement);
    setExpireDialogOpen(true);
  };

  const handleConfirmExpire = async () => {
    if (!expireTarget) return;
    try {
      setActionLoading(true);
      await cashSettlementService.adminForceExpire(expireTarget.id);
      enqueueSnackbar(t('adminCashSettlement.actions.forceExpireSuccess'), { variant: 'success' });
      setExpireDialogOpen(false);
      setExpireTarget(null);
      fetchList();
      fetchStats();
    } catch (err) {
      enqueueSnackbar(
        err?.message || t('adminCashSettlement.actions.forceExpireError'),
        { variant: 'error' }
      );
    } finally {
      setActionLoading(false);
    }
  };

  // ═════════════════════════════════════════════════
  // Helpers
  // ═════════════════════════════════════════════════

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: dateLocale });
  };

  const renderDueDate = (settlement) => {
    if (!settlement.due_date) return '-';
    const now = new Date();
    const due = new Date(settlement.due_date);
    const diffMs = due - now;

    if (settlement.status !== 'pending') {
      return (
        <Typography variant="caption">{formatDate(settlement.due_date)}</Typography>
      );
    }

    if (diffMs <= 0) {
      return (
        <Chip
          size="small"
          label={t('adminCashSettlement.table.expired')}
          color="error"
          variant="outlined"
          icon={<ErrorIcon />}
        />
      );
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const label = hours > 0
      ? t('adminCashSettlement.table.timeHoursMinutes', { hours, minutes })
      : t('adminCashSettlement.table.timeMinutes', { minutes });

    const color = hours < 48 ? 'warning' : 'info';
    return <Chip size="small" label={label} color={color} variant="outlined" icon={<AccessTimeIcon />} />;
  };

  const canResendPayer = (settlement) => {
    return settlement.status === 'pending';
  };

  const canResendRecipient = (settlement) => {
    return settlement.status === 'payer_confirmed';
  };

  const canForceExpire = (settlement) => {
    return settlement.status === 'pending';
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // ═════════════════════════════════════════════════
  // Render stats cards
  // ═════════════════════════════════════════════════

  const StatCard = ({ title, value, color, icon }) => (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          bgcolor: `${color}.lighter`,
          color: `${color}.main`,
          borderRadius: 2,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
            {statsLoading ? <CircularProgress size={24} /> : (value ?? '-')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  // ═════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyOffIcon fontSize="large" color="primary" />
            {t('adminCashSettlement.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('adminCashSettlement.subtitle')}
          </Typography>
        </Box>
        <Tooltip title={t('adminCashSettlement.refresh')}>
          <IconButton onClick={handleRefresh} color="primary" size="large">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards — Primary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('adminCashSettlement.stats.pending')}
            value={stats?.pending}
            color="warning"
            icon={<HourglassIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('adminCashSettlement.stats.payerConfirmed')}
            value={stats?.payer_confirmed}
            color="info"
            icon={<CheckCircleIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('adminCashSettlement.stats.expiringSoon')}
            value={stats?.expiring_soon}
            color="error"
            icon={<ScheduleIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('adminCashSettlement.stats.completed')}
            value={stats?.completed}
            color="success"
            icon={<TrendingUpIcon />}
          />
        </Grid>
      </Grid>

      {/* Stats Cards — Secondary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('adminCashSettlement.stats.expired')}
            value={stats?.expired}
            color="error"
            icon={<ErrorIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('adminCashSettlement.stats.disputed')}
            value={stats?.disputed}
            color="error"
            icon={<GavelIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('adminCashSettlement.stats.cancelled')}
            value={stats?.cancelled}
            color="default"
            icon={<CancelIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('adminCashSettlement.stats.total')}
            value={stats?.total}
            color="primary"
            icon={<MoneyOffIcon />}
          />
        </Grid>
      </Grid>

      {/* Alert for urgent items */}
      {stats && stats.expiring_soon > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <AlertTitle>{t('adminCashSettlement.alerts.urgentTitle')}</AlertTitle>
          <Typography variant="body2">
            {t('adminCashSettlement.alerts.expiringSoon', { count: stats.expiring_soon })}
          </Typography>
        </Alert>
      )}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('adminCashSettlement.filters.status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('adminCashSettlement.filters.status')}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                {STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder={t('adminCashSettlement.filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('adminCashSettlement.filters.sortBy')}</InputLabel>
              <Select
                value={sortBy}
                label={t('adminCashSettlement.filters.sortBy')}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
              >
                <MenuItem value="created_at">{t('adminCashSettlement.filters.sortCreatedAt')}</MenuItem>
                <MenuItem value="due_date">{t('adminCashSettlement.filters.sortDueDate')}</MenuItem>
                <MenuItem value="amount">{t('adminCashSettlement.filters.sortAmount')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('adminCashSettlement.filters.order')}</InputLabel>
              <Select
                value={sortOrder}
                label={t('adminCashSettlement.filters.order')}
                onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
              >
                <MenuItem value="DESC">{t('adminCashSettlement.filters.orderDesc')}</MenuItem>
                <MenuItem value="ASC">{t('adminCashSettlement.filters.orderAsc')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              type="date"
              label={t('adminCashSettlement.filters.dateFrom')}
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            <TextField
              size="small"
              type="date"
              label={t('adminCashSettlement.filters.dateTo')}
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            <TextField
              size="small"
              type="number"
              label={t('adminCashSettlement.filters.amountMin')}
              value={amountMin}
              onChange={(e) => { setAmountMin(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ width: 130 }}
            />
            <TextField
              size="small"
              type="number"
              label={t('adminCashSettlement.filters.amountMax')}
              value={amountMax}
              onChange={(e) => { setAmountMax(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ width: 130 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setStatusFilter('');
                setSearch('');
                setDateFrom('');
                setDateTo('');
                setAmountMin('');
                setAmountMax('');
                setSortBy('created_at');
                setSortOrder('DESC');
                setPage(1);
              }}
            >
              {t('adminCashSettlement.filters.reset')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Data Table */}
      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : settlements.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('adminCashSettlement.table.noResults')}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.lighter' }}>
                    <TableCell>{t('adminCashSettlement.table.dispute')}</TableCell>
                    <TableCell>{t('adminCashSettlement.table.payer')}</TableCell>
                    <TableCell>{t('adminCashSettlement.table.recipient')}</TableCell>
                    <TableCell>{t('adminCashSettlement.table.amount')}</TableCell>
                    <TableCell>{t('adminCashSettlement.table.status')}</TableCell>
                    <TableCell>{t('adminCashSettlement.table.dueDate')}</TableCell>
                    <TableCell>{t('adminCashSettlement.table.createdAt')}</TableCell>
                    <TableCell align="right">{t('adminCashSettlement.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settlements.map((s) => {
                    const chipConfig = STATUS_CHIP_CONFIG[s.status] || { color: 'default', icon: null };
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="caption" fontFamily="monospace">
                              #{s.dispute_id?.substring(0, 8)}
                            </Typography>
                            {s.reservation?.listing?.title && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
                                {s.reservation.listing.title}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{s.payer?.firstname} {s.payer?.lastname}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.payer?.email || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{s.recipient?.firstname} {s.recipient?.lastname}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.recipient?.email || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" color="primary.main">
                            {s.amount ? `${parseFloat(s.amount).toFixed(2)} €` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={chipConfig.icon}
                            label={t(`adminCashSettlement.statuses.${s.status}`)}
                            size="small"
                            color={chipConfig.color}
                          />
                        </TableCell>
                        <TableCell>
                          {renderDueDate(s)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(s.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title={t('adminCashSettlement.actions.openDispute')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => window.open(`/disputes/${s.dispute_id}`, '_blank')}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(canResendPayer(s) || canResendRecipient(s) || canForceExpire(s)) && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, s)}
                                disabled={actionLoading}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, p) => setPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {menuSettlement && canResendPayer(menuSettlement) && (
          <MenuItem onClick={() => handleResendPayer(menuSettlement)}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('adminCashSettlement.actions.resendPayer')}</ListItemText>
          </MenuItem>
        )}
        {menuSettlement && canResendRecipient(menuSettlement) && (
          <MenuItem onClick={() => handleResendRecipient(menuSettlement)}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('adminCashSettlement.actions.resendRecipient')}</ListItemText>
          </MenuItem>
        )}
        {menuSettlement && canForceExpire(menuSettlement) && (
          <MenuItem onClick={() => handleOpenExpireDialog(menuSettlement)}>
            <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{t('adminCashSettlement.actions.forceExpire')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Force Expire Confirmation Dialog */}
      <Dialog
        open={expireDialogOpen}
        onClose={() => !actionLoading && setExpireDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <BlockIcon color="error" />
          {t('adminCashSettlement.expireDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('adminCashSettlement.expireDialog.description')}
          </Typography>
          {expireTarget && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2">
                <strong>{t('adminCashSettlement.expireDialog.disputeId')}:</strong>{' '}
                {expireTarget.dispute_id?.substring(0, 12)}...
              </Typography>
              <Typography variant="body2">
                <strong>{t('adminCashSettlement.expireDialog.payer')}:</strong>{' '}
                {expireTarget.payer?.firstname} {expireTarget.payer?.lastname}
              </Typography>
              <Typography variant="body2">
                <strong>{t('adminCashSettlement.expireDialog.amount')}:</strong>{' '}
                {expireTarget.amount ? `${parseFloat(expireTarget.amount).toFixed(2)} €` : '-'}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpireDialogOpen(false)} disabled={actionLoading}>
            {t('adminCashSettlement.expireDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmExpire}
            variant="contained"
            color="error"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <BlockIcon />}
          >
            {t('adminCashSettlement.expireDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminCashSettlementPage;
